import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "emotion-theming";
import PropTypes from "prop-types";
import comparePriceToParent from "core-web/utils/comparePriceToParent";
import decodeHTMLEntities from "core-web/utils/decodeHTMLEntities";
import getProductStockStatus from "core-web/utils/getProductStockStatus";
import Above from "core-web/components/Breakpoints/Above";
import Image from "core-web/components/Image";
import Link from "core-web/components/Link";
import { PLACEHOLDER } from "theme/config/constants";
import styled from "theme/libs/styled";
import ProductVariantSelector from "theme/components/Variants/ProductVariantSelector";
import GoToProductPageButton from "theme/components/buttons/functionalButtons/GoToProductPageButton";
import AmountInput from "theme/components/products/AmountInput";
import Price from "theme/components/products/Price";
import TextStyles from "theme/components/text/TextStyles";
import RelationQuickShop from "./RelationQuickShop";

const Wrapper = styled("div")`
    flex: 1 0;
`;

const ProductCard = styled("div")`
    position: relative;
    display: grid;
    grid-template-columns: 88px 1fr;
    grid-template-rows: auto 52px;
    border-radius: 8px;
    padding: 8px;
    background-color: ${({ theme }) => theme.colors.white};
`;

const ImageWrapper = styled("div")`
    grid-row: span 2;
    border-radius: 8px;
    height: 117px;
`;

const Div = styled("div")`
    display: flex;
`;

const StyledAmountInput = styled(AmountInput)`
    position: absolute;
    right: 0;
    margin-right: 8px;
    top: -48px;
`;

const VariantSelectorSpace = styled("div")`
    height: 40px;
    width: 100%;
    margin-top: 12px;
`;

const RelationsProduct = ({ product, type, activeDropdown, handleMenu, i }) => {
    const { t } = useTranslation();
    const {
        name,
        image_key: image,
        manufacturer: { name: manufacturerName },
        description_header: descHeader,
        variants,
        price: priceExVat,
        price_standard: standardPriceExVat,
        vat_rate: vatRate,
        id,
        url,
        price_list_id: priceListId,
    } = product;
    const [selectedParametrics, setSelectedParametrics] = useState([]);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const { darkGreige } = useTheme().colors;
    const currentProduct = selectedVariant || product;
    const stockStatus = getProductStockStatus(currentProduct?.on_hand);
    const productName = decodeHTMLEntities(descHeader || name);
    const hasParametrics = !!variants.find((variant) => variant.variant_parametrics.length);
    const isVariant = product.variants.length > 1;
    const selectionHasStock = selectedVariant?.on_hand?.value > 0;
    const showDropdown = hasParametrics ? activeDropdown[i] : hasParametrics;
    const inStock = !!stockStatus.stock;

    for (const parametric of selectedParametrics) {
        if (parametric.code === "variant_value") {
            parametric.name = t("pdp.size");
        }
    }

    const getSelectedParametrics = (selectedParametrics) => {
        setSelectedParametrics(selectedParametrics);
    };

    const handleVariantSelection = (_productId, variant) => {
        if (variant) {
            setSelectedVariant(variant);
        } else {
            setSelectedVariant(null);
        }
    };

    return (
        <Wrapper key={id}>
            <Link to={url}>
                <ProductCard>
                    <ImageWrapper>
                        <Image
                            borderRadius="8px"
                            backgroundColor={darkGreige}
                            alt={name}
                            title={name}
                            src={{
                                url: image || PLACEHOLDER,
                                width: 88,
                                height: 117,
                            }}
                        />
                    </ImageWrapper>
                    <Div flexDirection="row" justifyContent="space-between" ml="16px">
                        <Div flexDirection="column" mr="10px">
                            <TextStyles mb="4px" textStyles="Buenos Aires/14-120 Regular">
                                {manufacturerName}
                            </TextStyles>
                            <TextStyles textStyles="Buenos Aires/14-120 SemiBold">{productName}</TextStyles>
                            <Price
                                mt="4px"
                                textStyles="Buenos Aires/14-120 Regular"
                                priceExVat={selectedVariant?.price || priceExVat}
                                priceListId={selectedVariant?.price_list_id || priceListId}
                                showFromPrice={isVariant && !selectedVariant}
                                standardPriceExVat={
                                    comparePriceToParent(selectedVariant?.included_products) ||
                                    selectedVariant?.price_standard ||
                                    standardPriceExVat
                                }
                                vatRate={vatRate}
                            />
                        </Div>
                        {inStock || isVariant ? (
                            <RelationQuickShop
                                position="absolute"
                                key={i}
                                isVariant={isVariant}
                                quantity={quantity}
                                handleMenu={() => handleMenu(i)}
                                selectedParametrics={selectedParametrics}
                                declaredVariant={selectedVariant}
                                product={product}
                            />
                        ) : (
                            <GoToProductPageButton buttonTheme="black_outlined" />
                        )}
                    </Div>
                    {isVariant && <VariantSelectorSpace />}
                </ProductCard>
            </Link>
            {isVariant && (
                <Above
                    breakpoint="tabletLg"
                    render={() => (
                        <Div key={i} position="relative">
                            <ProductVariantSelector
                                productId={product.id}
                                getSelectedParametrics={getSelectedParametrics}
                                layout="dropdown"
                                showDropdown={showDropdown}
                                variants={variants}
                                callback={handleVariantSelection}
                                selectedVariant={selectedVariant}
                                selectionHasStock={selectionHasStock}
                                type={type}
                            />
                            <StyledAmountInput
                                height="40px"
                                width="calc(clamp(100px, 15%, 240px) - 24px)"
                                maxLength={stockStatus?.stock}
                                isChangeable={!!stockStatus?.stock}
                                defaultValue={quantity ? parseInt(quantity, 10) : 1}
                                onChange={setQuantity}
                            />
                        </Div>
                    )}
                />
            )}
        </Wrapper>
    );
};

RelationsProduct.propTypes = {
    activeDropdown: PropTypes.object.isRequired,
    handleMenu: PropTypes.func.isRequired,
    i: PropTypes.number.isRequired,
    product: PropTypes.object.isRequired,
    type: PropTypes.string.isRequired,
};

export default RelationsProduct;
